import React from 'react';
import { Navbar, Container, Nav, Dropdown, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  Person,
  Gear,
  BoxArrowRight,
  PersonCircle,
  Building,
  ChevronDown,
  ArrowClockwise,
  Server,
  ShieldCheck,
  ExclamationTriangleFill,
  List
} from 'react-bootstrap-icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useAccount } from '../contexts/AccountContext';
import { useUser } from '../contexts/UserContext';
import { useGetEmailPlatformStatusQuery, useGetUnifiedEmailMonitoringQuery } from '../services/api';

const Navigation = ({ onMenuClick }) => {
  const {
    companyWorkspaces,
    selectedCompanyTenantId,
    workspaceLabel,
    selectWorkspace,
    isLoading,
  } = useAccount();
  const { currentUser } = useUser();
  const { logout } = useAuth0();

  const openSidebarMenu = () => {
    onMenuClick?.();
  };

  const handleLogout = async () => {
    try {
      // Clear all Auth0 storage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('@@auth0spajs@@')) {
          localStorage.removeItem(key);
        }
      });
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('@@auth0spajs@@')) {
          sessionStorage.removeItem(key);
        }
      });
      
      // Clear cached user data
      localStorage.removeItem('cachedUserName');
      
      // Determine the correct return URL based on environment
      const hostname = window.location.hostname;
      let returnTo = window.location.origin;
      
      // For production, return to production domain
      if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
        returnTo = 'https://krossglobalmarketing.com';
      } 
      // For staging, return to staging domain
      else if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
        returnTo = 'https://staging.krossglobalmarketing.com';
      }
      
      console.log('🔴 Logging out, returning to:', returnTo);
      
      // Call Auth0 logout
      logout({
        logoutParams: {
          returnTo: returnTo
        }
      });
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Force redirect even if logout fails
      window.location.href = window.location.origin;
    }
  };

  // Cache user name to prevent flicker on refresh
  const [cachedUserName, setCachedUserName] = React.useState(() => {
    try {
      return localStorage.getItem('cachedUserName') || '';
    } catch {
      return '';
    }
  });

  // Update cached user name when currentUser loads
  React.useEffect(() => {
    if (currentUser?.name) {
      setCachedUserName(currentUser.name);
      try {
        localStorage.setItem('cachedUserName', currentUser.name);
      } catch (e) {
        console.warn('Failed to save user name to localStorage:', e);
      }
    }
  }, [currentUser?.name]);

  // Use cached name during loading
  const displayName = currentUser?.name || cachedUserName || 'User';
  const { data: statusData } = useGetEmailPlatformStatusQuery(undefined, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    skipPollingIfUnfocused: true
  });
  const statusPayload = statusData?.data || statusData || null;
  const isDown = (configured, ok) => configured !== false && ok === false;
  const hasServerDown = Boolean(
    isDown(statusPayload?.inbound?.configured, statusPayload?.inbound?.queue?.ok) ||
      isDown(statusPayload?.smtpSubmission?.configured, statusPayload?.smtpSubmission?.ok) ||
      isDown(statusPayload?.smtp?.configured, statusPayload?.smtp?.tcp?.ok) ||
      isDown(statusPayload?.imap?.configured, statusPayload?.imap?.banner?.ok)
  );
  const authCanary = statusPayload?.imap?.authCanary || null;
  const remediationStatus = String(authCanary?.lastRemediationStatus || '');
  const remediationFailed =
    authCanary?.configured !== false &&
    remediationStatus &&
    remediationStatus.toLowerCase() !== 'success' &&
    !remediationStatus.toLowerCase().startsWith('skipped:');
  const lastFailureEpoch = Number(authCanary?.lastFailureEpoch || 0) || 0;
  const recentFailure = lastFailureEpoch > 0 && (Date.now() / 1000 - lastFailureEpoch) < 48 * 3600;
  const isSaturationHigh = statusPayload?.imap?.capacity?.sustainedHigh === true;
  const storageStatus = String(statusPayload?.storage?.status || '').toLowerCase();
  const isStorageDanger = storageStatus === 'danger';
  const isStorageWarning = storageStatus === 'warning';
  const hasDanger = hasServerDown || isStorageDanger || (remediationFailed && recentFailure);
  const hasWarning = isSaturationHigh || isStorageWarning || (!remediationFailed && recentFailure);
  const hasStatusData = Boolean(statusPayload && typeof statusPayload === 'object' && Object.keys(statusPayload).length > 0);
  const statusColor = hasDanger ? '#dc3545' : hasWarning ? '#fd7e14' : hasStatusData ? '#20c997' : '#adb5bd';
  const statusTitle = hasServerDown
    ? 'Server down - open Email Server Status'
    : isStorageDanger
    ? 'Mailbox storage danger - open Email Server Status'
    : isStorageWarning
    ? 'Mailbox storage saturated - open Email Server Status'
    : isSaturationHigh
    ? 'IMAP login saturation high - open Email Server Status'
    : remediationFailed && recentFailure
    ? 'IMAP drift remediation failed - open Email Server Status'
    : recentFailure
    ? 'IMAP drift detected recently - open Email Server Status'
    : hasStatusData
    ? 'All email servers healthy'
    : 'Email server status unknown';

  // Domain Health Monitor — aggregates per-domain SES identity / inbound MX / outbound routing checks.
  // Source: GET /monitoring/email-health (same data the Identity Health Monitoring page renders).
  // Pass no accountId so we cover ALL accounts globally for the nav icon.
  const { data: domainHealthData } = useGetUnifiedEmailMonitoringQuery(
    { timeRange: '24h' },
    {
      pollingInterval: 60_000,
      refetchOnFocus: true,
      skipPollingIfUnfocused: true,
    }
  );
  const domainHealthSummary = domainHealthData?.data?.summary || null;
  const hasDomainHealthData = Boolean(
    domainHealthSummary && typeof domainHealthSummary === 'object'
  );
  const domainsCriticalCount = Number(domainHealthSummary?.critical || 0);
  const domainsWarningCount = Number(domainHealthSummary?.warning || 0);
  const domainsHealthyCount = Number(domainHealthSummary?.healthy || 0);
  const hasDomainsCritical = domainsCriticalCount > 0;
  const hasDomainsWarning = domainsWarningCount > 0;
  const domainHealthColor = hasDomainsCritical
    ? '#dc3545'
    : hasDomainsWarning
    ? '#fd7e14'
    : hasDomainHealthData
    ? '#20c997'
    : '#adb5bd';

  // Pull the actual reason text for the top non-healthy domains so the tooltip
  // tells the user *which* domain is broken and *why*, instead of just showing
  // a count. We list at most 3 to keep the tooltip readable; the full breakdown
  // lives on the Domain Health Monitor page.
  const domainHealthIdentities = Array.isArray(domainHealthData?.data?.identities)
    ? domainHealthData.data.identities
    : [];
  const severityRank = { critical: 0, warning: 1, info: 2 };
  const flaggedDomainSamples = domainHealthIdentities
    .filter((d) => d?.status === 'critical' || d?.status === 'warning')
    .slice()
    .sort((a, b) => {
      const sa = a.status === 'critical' ? 0 : 1;
      const sb = b.status === 'critical' ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return Number(a.healthScore || 0) - Number(b.healthScore || 0);
    })
    .slice(0, 3)
    .map((d) => {
      const alerts = Array.isArray(d.alerts) ? d.alerts : [];
      if (alerts.length === 0) return `${d.name || d.domain}: ${d.status}`;
      const sorted = [...alerts].sort(
        (a, b) => (severityRank[a.type] ?? 99) - (severityRank[b.type] ?? 99)
      );
      const top = sorted[0];
      const reason = String(top?.message || '').trim();
      // Tighter truncation for nav tooltips — full text lives on the page.
      const truncated = reason.length > 110 ? `${reason.slice(0, 107)}...` : reason;
      return `${d.name || d.domain}: ${truncated}`;
    });
  const moreCount = Math.max(
    0,
    (domainsCriticalCount + domainsWarningCount) - flaggedDomainSamples.length
  );
  const domainHealthTitle = (() => {
    if (!hasDomainHealthData) return 'Domain health unknown - open Domain Health Monitor';
    if (!hasDomainsCritical && !hasDomainsWarning) {
      return `All ${domainsHealthyCount} domain${domainsHealthyCount === 1 ? '' : 's'} healthy - open Domain Health Monitor`;
    }
    const headline = hasDomainsCritical
      ? `${domainsCriticalCount} domain${domainsCriticalCount === 1 ? '' : 's'} CRITICAL`
      : `${domainsWarningCount} domain${domainsWarningCount === 1 ? '' : 's'} need attention`;
    const reasonsLine = flaggedDomainSamples.join('\n');
    const moreLine = moreCount > 0 ? `\n+${moreCount} more (open Domain Health Monitor)` : '';
    // Native `title` attribute respects \n so each domain renders on its own
    // line in the browser tooltip; no need for a heavy OverlayTrigger here.
    return `${headline}\n${reasonsLine}${moreLine}`;
  })();

  return (
    <Navbar variant="dark" expand="lg" sticky="top" className="shadow-sm kg-navbar-always-expanded" style={{ 
      height: '56px', // Fixed height to prevent flicker
      minHeight: '56px',
      maxHeight: '56px',
      padding: '0',
      backgroundColor: '#0a0e27'
    }}>
      <Container fluid className="h-100 d-flex align-items-center flex-nowrap">
        {/* Mobile menu toggle - button + touch handlers for iOS Safari compatibility */}
        {onMenuClick && (
          <Nav className="me-2 d-lg-none align-items-center flex-shrink-0">
            <button
              type="button"
              onClick={openSidebarMenu}
              onPointerUp={(e) => {
                if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                  e.preventDefault();
                  e.stopPropagation();
                  openSidebarMenu();
                }
              }}
              className="text-white border-0 bg-transparent d-flex align-items-center justify-content-center p-2"
              style={{
                minWidth: 44,
                minHeight: 44,
                cursor: 'pointer',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
              aria-label="Open menu"
            >
              <List size={24} />
            </button>
          </Nav>
        )}
        {/* Company workspace (multi-workspace = dropdown; single tenant = static label) */}
        <Nav className="me-auto kg-navbar-account-nav" style={{ minWidth: 0 }}>
          {(() => {
            const workspaceLabelInner = workspaceLabel || 'Workspace';
            const workspaceChrome = (
              <div className="d-flex align-items-center">
                <Building className="me-2 text-primary flex-shrink-0" size={18} />
                {isLoading ? (
                  <>
                    <ArrowClockwise className="me-2 text-white-50" size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    <span className="text-white-50 fw-medium kg-account-label">Loading...</span>
                  </>
                ) : (
                  <>
                    <span className="text-white fw-medium kg-account-label">{workspaceLabelInner}</span>
                    <Badge bg="success" className="ms-2 d-none d-md-inline-flex" style={{ fontSize: '0.7rem' }}>
                      active
                    </Badge>
                  </>
                )}
              </div>
            );
            const multiWorkspace = !isLoading && companyWorkspaces.length > 1;
            if (!multiWorkspace) {
              return (
                <div
                  className="d-flex align-items-center px-2 kg-account-toggle"
                  role="status"
                  aria-label={`Company workspace: ${workspaceLabelInner}`}
                >
                  {workspaceChrome}
                </div>
              );
            }
            return (
              <Dropdown className="account-selector">
                <Dropdown.Toggle
                  variant="dark"
                  id="account-dropdown"
                  className="d-flex align-items-center border-0 bg-transparent kg-account-toggle"
                  style={{ boxShadow: 'none' }}
                >
                  <div className="d-flex align-items-center">
                    <Building className="me-2 text-primary flex-shrink-0" size={18} />
                    {isLoading ? (
                      <>
                        <ArrowClockwise className="me-2 text-white-50" size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        <span className="text-white-50 fw-medium kg-account-label">Loading...</span>
                      </>
                    ) : (
                      <>
                        <span className="text-white fw-medium kg-account-label">{workspaceLabelInner}</span>
                        <Badge bg="success" className="ms-2 d-none d-md-inline-flex" style={{ fontSize: '0.7rem' }}>
                          active
                        </Badge>
                      </>
                    )}
                    <ChevronDown className="ms-2 text-white-50 d-none d-md-inline" size={14} />
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="dropdown-menu-start">
                  <Dropdown.Header>
                    <div className="d-flex align-items-center">
                      <Building className="me-2 text-primary" size={16} />
                      <span className="fw-bold">Company workspace</span>
                    </div>
                  </Dropdown.Header>
                  <Dropdown.Divider />

                  {companyWorkspaces.map((ws) => (
                    <Dropdown.Item
                      key={ws.companyTenantId}
                      onClick={() => selectWorkspace(ws.companyTenantId)}
                      className={selectedCompanyTenantId === ws.companyTenantId ? 'active' : ''}
                    >
                      <span className="fw-medium">{ws.displayName}</span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            );
          })()}
        </Nav>

        {/* User Menu - force row on mobile so status icon + avatar stay side-by-side */}
        <Nav className="ms-auto align-items-center flex-nowrap flex-row">
          <Nav.Link
            as={Link}
            to="/health-monitoring"
            title={domainHealthTitle}
            aria-label={domainHealthTitle}
            className="d-flex align-items-center me-2 px-2 flex-shrink-0"
            style={{ color: domainHealthColor, position: 'relative' }}
          >
            <ShieldCheck size={18} />
            {(hasDomainsCritical || hasDomainsWarning) && (
              <ExclamationTriangleFill
                size={10}
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 2,
                  color: domainHealthColor
                }}
              />
            )}
          </Nav.Link>
          <Nav.Link
            as={Link}
            to={isStorageDanger || isStorageWarning ? '/admin/email-platform-status#storage-status' : '/admin/email-platform-status'}
            title={statusTitle}
            aria-label={statusTitle}
            className="d-flex align-items-center me-2 px-2 flex-shrink-0"
            style={{ color: statusColor, position: 'relative' }}
          >
            <Server size={18} />
            {(hasDanger || hasWarning) && (
              <ExclamationTriangleFill
                size={10}
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 2,
                  color: statusColor
                }}
              />
            )}
          </Nav.Link>
          {/* Always render user menu to prevent navbar height change */}
          <Dropdown align="end" className="flex-shrink-0">
            <Dropdown.Toggle 
              variant="dark" 
              id="user-dropdown"
              className="d-flex align-items-center border-0 bg-transparent"
              style={{ boxShadow: 'none' }}
            >
              <div className="d-flex align-items-center">
                {currentUser?.avatar ? (
                  <img 
                    src={currentUser.avatar} 
                    alt={displayName}
                    className="rounded-circle me-2"
                    style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                    onError={(e) => {
                      // Replace image with fallback icon on error
                      e.target.outerHTML = '<div class="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2" style="width: 32px; height: 32px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" class="bi bi-person-circle" viewBox="0 0 16 16"><path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/><path fill-rule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z"/></svg></div>';
                    }}
                  />
                ) : (
                  <div 
                    className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2"
                    style={{ width: '32px', height: '32px', flexShrink: 0 }}
                  >
                    <PersonCircle size={16} className="text-white" />
                  </div>
                )}
                <span className="text-white d-none d-md-inline" style={{ fontSize: '0.84rem', minWidth: '80px' }}>
                  {currentUser ? displayName : (cachedUserName || 'Loading...')}
                </span>
              </div>
            </Dropdown.Toggle>

            {currentUser && (
              <Dropdown.Menu className="dropdown-menu-end" style={{ fontSize: '0.84rem' }}>
                <Dropdown.Header>
                  <div className="text-center">
                    {currentUser.avatar ? (
                      <img 
                        src={currentUser.avatar} 
                        alt={displayName}
                        className="rounded-circle mx-auto mb-2"
                        style={{ width: '48px', height: '48px', objectFit: 'cover', display: 'block' }}
                        onError={(e) => {
                          // Replace image with fallback icon on error
                          e.target.outerHTML = '<div class="bg-primary rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style="width: 48px; height: 48px;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="white" class="bi bi-person-circle" viewBox="0 0 16 16"><path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/><path fill-rule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z"/></svg></div>';
                        }}
                      />
                    ) : (
                      <div 
                        className="bg-primary rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                        style={{ width: '48px', height: '48px' }}
                      >
                        <PersonCircle size={20} className="text-white" />
                      </div>
                    )}
                    <div className="fw-bold" style={{ fontSize: '0.84rem' }}>{displayName}</div>
                    {currentUser.email && (
                      <div className="text-muted" style={{ fontSize: '0.78rem' }}>{currentUser.email}</div>
                    )}
                  </div>
                </Dropdown.Header>
                <Dropdown.Divider />
                <Dropdown.Item as={Link} to="/profile?tab=personal" style={{ fontSize: '0.84rem' }}>
                  <Person className="me-2" size={16} />
                  Profile
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/profile?tab=preferences" style={{ fontSize: '0.84rem' }}>
                  <Gear className="me-2" size={16} />
                  Settings
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item className="text-danger" onClick={handleLogout} style={{ fontSize: '0.84rem' }}>
                  <BoxArrowRight className="me-2" size={16} />
                  Sign Out
                </Dropdown.Item>
              </Dropdown.Menu>
            )}
          </Dropdown>
        </Nav>
      </Container>
    </Navbar>
  );
};

export default Navigation;

